import * as coreEntities from 'core/store/index';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import resolveProgramIconUrl from '../../../utils/resolveProgramIconUrl';

import type { CourseAPIResponse } from 'app/types/courses';
import type { PracticeCourse } from '../../../types/practiceCourses';

type UseSubjectSelectionDialogParams = {
  courses: PracticeCourse[];
  isOpen: boolean;
  onConfirm: (course: PracticeCourse) => void;
};

type UseSubjectSelectionDialogResult = {
  courseIconUrls: Record<string, string | undefined>;
  handleConfirm: () => void;
  handleSelectCourse: (courseId: string) => void;
  hasCourses: boolean;
  isConfirmDisabled: boolean;
  selectedCourseId: string | null;
  t: (key: string) => string;
};

/**
 * Single-selection logic for the subject (Popup 1) dialog (TANAI-1869).
 */
function useSubjectSelectionDialog({ courses, isOpen, onConfirm }: UseSubjectSelectionDialogParams): UseSubjectSelectionDialogResult {
  const { t } = useTranslation(['chatbot', 'actions']);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  const cachedCourses = useSelector(
    (state: unknown) => coreEntities.courses.selectors.getCourses(state) as CourseAPIResponse[] | null
  );

  const courseIconUrls = useMemo(() => {
    const iconUrlsByCourseId: Record<string, string | undefined> = {};
    courses.forEach((course) => {
      iconUrlsByCourseId[course.id] = resolveProgramIconUrl(course.id, cachedCourses);
    });
    return iconUrlsByCourseId;
  }, [courses, cachedCourses]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCourseId(null);
    }
  }, [isOpen]);

  const handleSelectCourse = useCallback((courseId: string) => {
    setSelectedCourseId(courseId);
  }, []);

  const handleConfirm = useCallback(() => {
    const course = courses.find((item) => item.id === selectedCourseId);
    if (course) {
      onConfirm(course);
    }
  }, [courses, onConfirm, selectedCourseId]);

  const hasCourses = courses.length > 0;
  const isConfirmDisabled = !selectedCourseId;

  return {
    courseIconUrls,
    handleConfirm,
    handleSelectCourse,
    hasCourses,
    isConfirmDisabled,
    selectedCourseId,
    t,
  };
}

export default useSubjectSelectionDialog;
